import { FC, useCallback, useEffect, useState, useMemo, useRef, KeyboardEvent as ReactKeyboardEvent } from 'react'
import cn from 'classnames'
import styles from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout.styl'
import { ReactNode } from 'react-redux'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import Scrollable from 'components/atoms/Scrollable'
import SvgIcon from 'components/atoms/SvgIcon'
import CrossIcon from 'icons/cross.svg'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { FloatingPane } from 'interfaces/floatingPanes'
import { PreventScroll } from 'components/features/pfm/PreventScroll/PreventScroll'
import { FitToHeightViewport } from 'components/atoms/inputs/Select/FitToHeight/FitToHeight'
import { SwipeHandler } from 'handlers/SwipeHandler/SwipeHandler'
import { animated, config, useTransition } from '@react-spring/web'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'

export const CLOSE_PANE_BUTTON_ID = 'closePaneButton'

export type FloatingPaneLayoutProps = {
  type: FloatingPane['type']

  notification?: ReactNode
  header?: ReactNode
  footer?: ReactNode
  wrapperClassName?: string
  bodyClassName?: string
  footerClassName?: string
  withStaticHeader?: boolean
  withStaticFooter?: boolean
  cardLayout?: boolean
  multiCardLayout?: boolean
  verticallySpreadLayout?: boolean
  onClose?: () => void
  onBack?: () => void
  onSwipeRight?: () => void
}

export const FloatingPaneLayout: FC<FloatingPaneLayoutProps> = (props) => {
  const {
    type,
    notification,
    header,
    footer,
    bodyClassName,
    wrapperClassName,
    footerClassName,
    children,
    onBack,
    onClose,
    onSwipeRight,
    withStaticHeader,
    withStaticFooter,
    cardLayout,
    multiCardLayout,
    verticallySpreadLayout,
  } = props

  const dispatch = useStoreDispatch()
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
      return
    }

    dispatch(killFloatingPanesActionCreator(type))
  }, [dispatch, onClose, type])

  const headerChild = useMemo(() => {
    if (!header) {
      return null
    }
    return (
      <header className={ styles.header }>
        { header }
      </header>
    )
  }, [header])

  const footerChild = useMemo(() => {
    if (!footer) {
      return null
    }

    return (
      <footer className={ cn(styles.footer, footerClassName) }>
        { footer }
      </footer>
    )
  }, [footer, footerClassName])

  const bodyChild = useMemo(() => !!cardLayout ? (
    <main className={ styles.cards }>
      <CardBlock
        forceRoundedCorners
        shadowTheme='medium'
        withPadding
        className={ bodyClassName }
        children={ children }
      />
    </main>
  ) : (
    <main className={ bodyClassName || styles.body }>
      { children }
    </main>
  ), [cardLayout, bodyClassName, children])

  const notificationTransitions = useTransition(notification, {
    config: config.gentle,
    from: { transform: `translateY(-100%)`, opacity: 0, height: 0 },
    enter: { transform: `translateY(0px)`, opacity: 1, height: 'auto' },
    leave: { transform: `translateY(-100%)`, opacity: 0, height: 0 },
  })

  const notificationChild = useMemo(() => notificationTransitions(
    (animationStyles, animatedContent) => !!animatedContent && (
      <animated.div className={ styles.notification } style={ animationStyles }>
        { animatedContent }
      </animated.div>
    )
  ), [notificationTransitions])

  const closeButton = useMemo(() => {
    return onClose && (
      <button className={ styles.cross } onClick={ handleClose } type='button' id={ CLOSE_PANE_BUTTON_ID }>
        <SvgIcon src={ CrossIcon } />
      </button>
    )
  }, [onClose, handleClose])

  const contentClassNames = cn({
    [styles.verticallySpreadLayout]: !!verticallySpreadLayout,
  })

  const contentChild = useMemo(() => (
    <>
      { !withStaticHeader && closeButton }
      { !withStaticHeader && headerChild }
      { !withStaticHeader && notificationChild }
      <FitToHeightViewport>
        <Scrollable contentClassName={ contentClassNames } allowOuterScroll flexBox>
          { !!withStaticHeader && closeButton }
          { !!withStaticHeader && headerChild }
          { !!withStaticHeader && notificationChild }
          { bodyChild }
          { !!withStaticFooter && footerChild }
        </Scrollable>
      </FitToHeightViewport>
      { !withStaticFooter && footerChild }
    </>
  ), [withStaticHeader, headerChild, notificationChild, contentClassNames, closeButton, bodyChild, withStaticFooter, footerChild])

  // TODO - Get rid of 'onBack' and 'handleClose' use only 'onSwipeRight'
  const handleSwipeRight = useMemo(() => onSwipeRight || onBack || handleClose, [handleClose, onBack, onSwipeRight])

  const handleKeydown = useCallback<(e: ReactKeyboardEvent<HTMLElement>) => void>((event) => {
    if (event.key !== 'Escape') {
      return
    }

    handleClose()
  }, [handleClose])

  // For case when floating panel don't have any elements that can receive focus we have to handle Escape event on document
  // If the active element is inside the panel than ignore this event, because onKeyDown handler will be invoked
  const rootNodeRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      const { current } = rootNodeRef
      if (event.key !== 'Escape' || !current || current.contains(document.activeElement)) {
        return
      }

      handleClose()
    }

    document.addEventListener('keydown', handle, false)

    return () => {
      document.removeEventListener('keydown', handle, false)
    }
  }, [handleClose])

  const [innerHeight, setInnerHeight] = useState(window.innerHeight)
  useEffect(() => {
    const handle = () => {
      setInnerHeight((prevState) => {
        if (prevState === window.innerHeight) {
          return prevState
        }
        return window.innerHeight
      })
    }

    window.addEventListener('resize', handle)
    return () => {
      window.removeEventListener('resize', handle)
    }
  }, [])

  const floatingPaneClasses = cn(styles.floatingPaneLayout, wrapperClassName, {
    [styles.withStaticHeader]: !!withStaticHeader,
    [styles.withStaticFooter]: !!withStaticFooter,
    [styles.multiCardLayout]: !!multiCardLayout,
    [styles.cardLayout]: !!cardLayout,
  })

  // `window.innerHeight` works on mobile when address bar begins changing it's size
  // https://chanind.github.io/javascript/2019/09/28/avoid-100vh-on-mobile-web.html
  return useMemo(() => (
    <SwipeHandler onSwipedRight={ handleSwipeRight }>
      <div
        ref={ rootNodeRef }
        className={ floatingPaneClasses }
        style={{ height: innerHeight }}
        onKeyDown={ handleKeydown }
      >
        { contentChild }
        <PreventScroll />
      </div>
    </SwipeHandler>
  ), [handleSwipeRight, floatingPaneClasses, innerHeight, handleKeydown, contentChild])
}
